<script setup lang="ts">
/**
 * Wave 1 — Property access modals (Agent C).
 *
 * Verify gate fired from the availability tab/action when a registered
 * (but unverified) agent tries to see live unit data.
 */

const { t } = useI18n();

const { userPhoneMasked = '+971 50 ••• ••24' } = defineProps<{
  userPhoneMasked?: string;
}>();

const emit = defineEmits<{
  (e: 'close'): void;
  (e: 'verify-start'): void;
  (e: 'change-number'): void;
}>();

const titleId = useId();
</script>

<template>
  <ModalShell
    accent="green"
    :labelled-by="titleId"
    @close="emit('close')"
  >
    <ModalIcon
      kind="availability"
      accent="green"
    />
    <div class="mb-2 font-mono text-[10px] font-bold uppercase tracking-[0.12em] text-success">
      {{ t('property.access.modalVerifyAvailability.stepBadge') }}
    </div>
    <h2
      :id="titleId"
      class="mb-3 font-serif text-[24px] font-semibold leading-[1.15] tracking-[-0.015em] text-black-100"
    >
      {{ t('property.access.modalVerifyAvailability.heading') }}
    </h2>
    <p class="mb-4 text-[13.5px] leading-[1.55] text-black-70">
      {{ t('property.access.modalVerifyAvailability.body') }}
    </p>

    <div class="mb-5 rounded-2xl border border-success/25 bg-success/05 px-5 py-4 text-center">
      <div class="mb-2 font-mono text-[10px] font-bold uppercase tracking-[0.1em] text-black-60">
        {{ t('property.access.modalVerifyAvailability.nextStepLabel') }}
      </div>
      <div class="flex justify-center gap-1.5">
        <div
          v-for="(cell, idx) in ['5', '3', '8', '·', '·', '·']"
          :key="idx"
          class="flex h-11 w-9 items-center justify-center rounded-lg border border-black-10 bg-white text-[18px] font-bold text-black-100"
          :class="{ 'text-black-30': cell === '·' }"
        >
          {{ cell }}
        </div>
      </div>
    </div>

    <ul class="mb-6 space-y-2">
      <CheckBullet accent="green">{{ t('property.access.modalVerifyAvailability.bullet1') }}</CheckBullet>
      <CheckBullet accent="green">{{ t('property.access.modalVerifyAvailability.bullet2') }}</CheckBullet>
      <CheckBullet accent="green">{{ t('property.access.modalVerifyAvailability.bullet3') }}</CheckBullet>
      <CheckBullet accent="green">{{ t('property.access.modalVerifyAvailability.bullet4') }}</CheckBullet>
    </ul>

    <div class="flex flex-col gap-2">
      <button
        type="button"
        class="inline-flex h-11 items-center justify-center rounded-[10px] bg-success px-5 text-[14px] font-bold text-white transition-colors hover:bg-success/90"
        @click="emit('verify-start')"
      >
        <span
          >{{ t('property.access.modalVerifyAvailability.verifyPhoneArrow') }}
          <span
            class="inline-block rtl:-scale-x-100"
            aria-hidden="true"
            >→</span
          ></span
        >
      </button>
      <button
        type="button"
        class="inline-flex h-10 items-center justify-center rounded-[10px] text-[13px] font-semibold text-black-80 transition-colors hover:bg-black-05"
        @click="emit('close')"
      >
        {{ t('property.access.shared.maybeLater') }}
      </button>
    </div>
    <div class="mt-4 text-center text-[11.5px] text-black-60">
      <span dir="ltr">{{ userPhoneMasked }}</span> ·
      <button
        type="button"
        class="font-semibold text-black-80 underline-offset-2 hover:underline"
        @click="emit('change-number')"
      >
        {{ t('property.access.shared.changeNumber') }}
      </button>
    </div>
  </ModalShell>
</template>
